import { useMemo } from 'react';

import { OptimizationWorkload, WorkloadScalingPolicy } from '@cast/types';

import { useWorkloads } from './useWorkloads';
import { useScalingPolicies } from './useScalingPolicies';

export type ScalingPolicyWithWorkloads = WorkloadScalingPolicy & {
  workloads: OptimizationWorkload[];
};

type UseScalingPoliciesWithWorkloadsArgs = {
  enabled?: boolean;
};

export const useScalingPoliciesWithWorkloads = ({
  enabled = true,
}: UseScalingPoliciesWithWorkloadsArgs = {}) => {
  const {
    workloads,
    isLoading: workloadsIsLoading,
    isFetching: workloadsIsFetching,
    error: workloadsError,
    refetch: refetchWorkloads,
  } = useWorkloads({ enabled });

  const {
    policies: _policies,
    isLoading: policiesIsLoading,
    isFetching: policiesIsFetching,
    error: policiesError,
    refetch: refetchPolicies,
  } = useScalingPolicies({ enabled });

  const policies = useMemo<ScalingPolicyWithWorkloads[]>(() => {
    return workloads && _policies
      ? _policies.map((policy) => ({
          ...policy,
          workloads: workloads.filter(
            ({ scalingPolicyId }) => policy.id === scalingPolicyId
          ),
        }))
      : [];
  }, [_policies, workloads]);

  const refetch = () => {
    void refetchWorkloads();
    void refetchPolicies();
  };

  return {
    policies,
    isLoading: workloadsIsLoading || policiesIsLoading,
    isFetching: workloadsIsFetching || policiesIsFetching,
    hasError: !!workloadsError || !!policiesError,
    refetch,
  };
};
